<template>
  <v-menu
    ref="datePickerMenu"
    v-model="menu"
    width="auto"
    transition="scale-transition"
    :close-on-content-click="false"
  >
    <template #activator="{ props }">
      <AGTextField
        :id="id"
        append-icon="mdi-calendar-outline"
        autocomplete="off"
        clearable
        readonly
        :model-value="textFieldModel"
        :disabled="disabled"
        :hide-details="hideDetails"
        :hint="hint"
        :label="label"
        :persistent-placeholder="persistentPlaceholder"
        :placeholder="placeholder"
        :required="required"
        :rules="rules"
        :message-color="messageColor"
        v-bind="props"
        @click:clear="onClear"
      />
    </template>
    <v-date-picker
      v-model="datePickerModel"
      v-click-outside="onCancel"
      no-title
      hide-header
      scrollable
      :max="max"
      :min="min"
    >
      <template #actions>
        <AGButton type="tertiary" @click="onCancel"> Cancelar </AGButton>
        <AGButton type="primary" :disabled="!hasSelectedValue" @click="onConfirm"> Confirmar </AGButton>
      </template>
    </v-date-picker>
  </v-menu>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGTextField from '@/components/common/AGTextField.vue';
import { MESSAGE_COLOR } from '@/constants/ConstantMessageColor';
import UtilsDate from '@/utils/UtilsDate';
import { uniqueId } from 'lodash';

export default {
  emits: ['update:modelValue'],

  components: {
    AGTextField,
    AGButton,
  },

  props: {
    id: {
      type: String,
      default: uniqueId('agDatePicker'),
    },

    label: {
      type: String,
      require: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
      default: 'Selecione uma data',
    },

    persistentPlaceholder: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: [Date, String, null],
      default: undefined,
    },

    min: {
      type: [Date, String],
    },

    max: {
      type: [Date, String],
    },

    required: {
      type: Boolean,
      default: false,
    },

    rules: {
      type: Array,
      default: () => [],
    },

    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },

    hint: {
      type: String,
      default: '',
    },

    valueFormat: {
      type: String,
      default: 'YYYY-MM-DD',
    },

    displayFormat: {
      type: String,
      default: 'DD/MM/YYYY',
    },

    messageColor: {
      type: String,
      default: MESSAGE_COLOR.DEFAULT,
      validator: (val) => Object.values(MESSAGE_COLOR).includes(val),
    },
  },

  data: () => ({
    menu: false,
    datePickerModel: null,
  }),

  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },

    textFieldModel() {
      return UtilsDate.format2(this.datePickerModel, {
        fromFormat: undefined,
        toFormat: this.displayFormat || this.valueFormat,
      });
    },

    hasSelectedValue() {
      return this.datePickerModel !== null;
    },
  },

  watch: {
    localValue(newVal) {
      this.datePickerModel = UtilsDate.toDate(newVal, this.valueFormat);
    },
  },

  mounted() {
    this.update();
  },

  methods: {
    closeMenu() {
      this.menu = false;
    },
    onCancel() {
      this.closeMenu();
      this.update();
    },
    onConfirm() {
      this.closeMenu();
      this.localValue = UtilsDate.format(this.datePickerModel, this.valueFormat);
    },
    onClear() {
      this.datePickerModel = null;
      this.localValue = null;
    },
    update() {
      this.datePickerModel = UtilsDate.toDate(this.localValue, this.valueFormat);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/datepicker.scss';
</style>
